import { useEffect, useRef } from 'react'
import { Outlet } from 'react-router-dom'
import GenerateWidget from '../../generate/components/GenerateWidget.tsx'
import { BotsWidgetProvider, useBotsWidget } from '../context/BotsWidgetContext.tsx'
import type { Model } from '../../../data/models.ts'

// Ряд иконок-вкладок над панелью (Тренды / Изображения / Видео / Аудио)
// удалён по решению владельца 2026-07-27: он дублировал те же четыре пункта
// сайдбара, стоявшего рядом на том же экране. Навигация по разделам —
// только сайдбар (на мобилке — его выдвижная панель).

interface Props {
	onSelectModel?: (model: Model | null) => void
	/** 'bare' — без стеклянной панели раздела и её сетки: страница ложится
	 *  прямо на фон, как /generate (студия презентаций, решение владельца
	 *  02.09). Виджет и запас под него — те же. */
	frame?: 'panel' | 'bare'
}

function BotsMediaContent({ frame }: { frame: 'panel' | 'bare' }) {
	const {
		widgetModel,
		widgetRef,
		isCollapsed,
		handleCollapse,
		handleExpand,
		handleModelChange,
		handleCreateVideoRequest,
		handlePersonaChange,
	} = useBotsWidget()

	// Виджет — fixed-панель над низом окна: без запаса контент страницы
	// навсегда прятался под ним (чтобы выбрать из ленты, приходилось
	// сворачивать виджет). Меряем живую высоту .gw (раскрыт/свёрнут/
	// tool-режим) и отдаём панели как --bots-widget-h → padding-bottom.
	// .gw ищем от document: GenerateWidget рендерится порталом в body.
	const layoutRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const layout = layoutRef.current
		const gw = document.querySelector<HTMLElement>('.gw')
		if (!layout) return
		if (!widgetModel || !gw) {
			layout.style.removeProperty('--bots-widget-h')
			return
		}
		const sync = () => layout.style.setProperty('--bots-widget-h', `${gw.offsetHeight}px`)
		sync()
		// Дебаунс: при анимации сворачивания/разворота (grid-rows transition
		// ~0.4s) ResizeObserver стреляет на КАЖДЫЙ кадр, а запись переменной
		// инвалидирует layout всей страницы (padding-bottom панели зависит от
		// неё) — рефлоу страницы на каждом кадре лагал заметнее всего с
		// высоким tool-режимом. Пишем финальное значение после затишья;
		// промежуточные кадры анимации переменную не трогают.
		let t: number | undefined
		const debouncedSync = () => {
			if (t !== undefined) window.clearTimeout(t)
			t = window.setTimeout(sync, 140)
		}
		const ro = new ResizeObserver(debouncedSync)
		ro.observe(gw)
		return () => {
			if (t !== undefined) window.clearTimeout(t)
			ro.disconnect()
			layout.style.removeProperty('--bots-widget-h')
		}
	}, [widgetModel])

	return (
		<>
			<div className={`bots-media-layout${frame === 'bare' ? ' bots-media-layout--bare' : ''}`} ref={layoutRef}>
				{frame === 'bare' ? (
					<Outlet />
				) : (
					<div className="bots-media-panel">
						<div className="bots-ambient" aria-hidden="true" />
						<Outlet />
					</div>
				)}
			</div>

			{/* Виджет всегда смонтирован пока widgetModel != null. Коллапс/раскрытие —
			    через `isCollapsed` prop, не через mount/unmount. Это сохраняет
			    внутренний state (промпт, uploads, результат) между сворачиваниями
			    и даёт плавную collapse-анимацию внутри одного DOM-дерева. */}
			{widgetModel && (
				<GenerateWidget
					ref={widgetRef}
					key="bots-widget"
					model={widgetModel}
					isCollapsed={isCollapsed}
					onClose={handleCollapse}
					onExpandRequest={handleExpand}
					onModelChange={handleModelChange}
					onCreateVideoRequest={handleCreateVideoRequest}
					onPersonaChange={handlePersonaChange}
				/>
			)}
		</>
	)
}

export default function BotsMediaLayout({ onSelectModel, frame = 'panel' }: Props) {
	return (
		<BotsWidgetProvider onSelectModel={onSelectModel}>
			<BotsMediaContent frame={frame} />
		</BotsWidgetProvider>
	)
}
